import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import type { Layer } from '@deck.gl/core'

type DeckViewport = {
  project: (lngLat: [number, number]) => [number, number]
}

type DeckInstanceLike = {
  getCanvas: () => HTMLCanvasElement | null
  getViewports: () => DeckViewport[]
}

export type GoogleDeckDomEvent = google.maps.MapMouseEvent['domEvent']

type GoogleMapMouseEventWithDom = google.maps.MapMouseEvent & {
  domEvent?: GoogleDeckDomEvent
  pixel?: { x: number; y: number }
}

function hasClientPoint(
  domEvent: GoogleDeckDomEvent | undefined
): domEvent is MouseEvent | PointerEvent {
  return (
    typeof (domEvent as MouseEvent | PointerEvent | undefined)?.clientX ===
      'number' &&
    typeof (domEvent as MouseEvent | PointerEvent | undefined)?.clientY ===
      'number'
  )
}

export type GoogleDeckPickResult = {
  layer: Layer | null
  layerId: string | null
  object?: unknown
  pixel: { x: number; y: number } | null
  picked: boolean
}

export type GoogleDeckClickResult = GoogleDeckPickResult & {
  domEvent: GoogleDeckDomEvent | null
  latLng: google.maps.LatLngLiteral | null
}

export function getGoogleDeckPickingPixel(
  overlay: GoogleMapsOverlay,
  event: google.maps.MapMouseEvent
) {
  const deck = (overlay as unknown as { _deck?: DeckInstanceLike })._deck
  const { domEvent, latLng, pixel } = event as GoogleMapMouseEventWithDom

  if (deck && hasClientPoint(domEvent)) {
    const canvas = deck.getCanvas()

    if (canvas) {
      const rect = canvas.getBoundingClientRect()

      return {
        x: domEvent.clientX - rect.left,
        y: domEvent.clientY - rect.top,
      }
    }
  }

  if (deck && latLng) {
    const viewport = deck.getViewports()[0]

    if (viewport) {
      const [x, y] = viewport.project([latLng.lng(), latLng.lat()])

      return { x, y }
    }
  }

  return pixel ?? null
}

export function pickGoogleDeckObject(
  overlay: GoogleMapsOverlay,
  event: google.maps.MapMouseEvent,
  radius: number
): GoogleDeckPickResult {
  const pixel = getGoogleDeckPickingPixel(overlay, event)

  if (!pixel) {
    return {
      layer: null,
      layerId: null,
      picked: false,
      pixel: null,
    }
  }

  const picked = overlay.pickObject({
    radius,
    x: pixel.x,
    y: pixel.y,
  })

  return {
    layer: (picked?.layer as Layer | null | undefined) ?? null,
    layerId: picked?.layer?.id ?? null,
    object: picked?.object,
    picked: Boolean(picked?.picked),
    pixel,
  }
}

export function pickGoogleDeckClick(
  overlay: GoogleMapsOverlay,
  event: google.maps.MapMouseEvent,
  radius: number
): GoogleDeckClickResult {
  return {
    ...pickGoogleDeckObject(overlay, event, radius),
    domEvent: event.domEvent ?? null,
    latLng: event.latLng?.toJSON() ?? null,
  }
}
